import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	Blade,
	Button,
	FileImport,
	ProgressIndicator,
	ErrorBanner
} from "rhombus-react";

import { closeModal } from 'reducers/Modal';
import { Assets } from 'reducers/Assets';

import { API_URL, MODAL_TYPES } from 'utils/constants';
import { pluralize } from 'utils/common';

import "./UploadTextAssetModal.css";

export function uploadNotify({ data }) {
	const id = new Date().getTime();

	const BAD_REQ = 400;
	if(data.code >= BAD_REQ){
		return {
			id,
			type:"error",
			message:"Unable to import Text Assets",
		};
	}

	if (data.newAssets.length === 0) {
		return {
			id,
			type: "warning",
			message: "No new Text Assets to import",
		};
	}

	let type = "success"
		, message = `${data.newAssets.length} new ${pluralize("Text Asset", data.newAssets.length)} imported`
		, messageHeader = "Imported Assets";

	return {
		id,
		type,
		message,
		messageHeader,
	};
}

export default function UploadTextAssetModal() {
	const { modal, libraryProperties } = useSelector((state) => {
		return {
			modal: state.modal,
			libraryProperties: state.libraryProperties,
		};
	});
	const { id: libraryId } = libraryProperties;

	const dispatch = useDispatch();
	const [isUploading, setIsUploading] = useState(false);
	const [formData, setFormData] = useState();/*
		FormData to be sent with a multipart/form-data POST request.

		The API's `import` endpoints want FormData with an entry for the file, keyed as 'file',

		Like:
			FormData: [
				{ file: File },
			]
	*/

	const [errorMessage, setErrorMessage] = useState("");

	// guard clause
	if (!modal.isOpen || modal.modalType !== MODAL_TYPES.UPLOAD_TEXT_ASSET) { return null; }

	function handleUpload() {
		if (!formData) { return; }

		setIsUploading(true);

		let meta = {
			buildMessage: uploadNotify,
		};

		dispatch(Assets.uploadAssets('text', libraryId, formData, { meta }))
			.then((response) => {
				setIsUploading(false);
				if(response.success){
					const { newAssets } = response.payload.data;
					const newItems = newAssets.map(({ id, text, lastModified }) => ({
						assetType: "Text",
						name: text,
						lastModified,
						id,
					}));

					dispatch(Assets.addSocketAssets(libraryProperties, newItems));
					onClose();
				} else {
					let { errorObj = { name: "Upload Error", description: null } } = response.payload;
					let description = errorObj.description ? `: ${errorObj.description}` : null;
					setErrorMessage(`${errorObj.name}${description}`);
				}
			});
	}

	function handleFileSelectionChange(changeList) {
		setErrorMessage(null);

		// See this component's `formData` state declaration for details on this structuring.
		 if (changeList[0].formData) {
			 setFormData(changeList[0].formData);
		 }
	}

	function resetState() {
		setErrorMessage("");
		setIsUploading(false);
		setFormData(null);
	}

	function onClose() {
		resetState();
		dispatch(closeModal());
	}

	const renderContent = () => {
		let $errorDisplay = errorMessage && (
			<ErrorBanner
				title = {errorMessage}
			/>
		);

		const fileImportProps = {
			label: "Select File",
			required: true,
			inputPlaceholder: "Click browse to select spreadsheet",
			useField: false,
			multipleFiles: false,
			onChange: handleFileSelectionChange,
			accept: ".xlsx",
		};

		const url = `${API_URL}/crm/v1/creative-libraries/${libraryId}/text/import-template`;
		const aProps = {
			href:url,
			className:"download-template",
			target:"_BLANK",
			rel:"noreferrer noopener",
		};

		const $content = (
			<>
				<div className="upload-text-asset-header">
					Upload text assets via spreadsheet | <a {...aProps} download>Download template</a>
				</div>
				<FileImport {...fileImportProps} />
			</>
		);

		return(
			<div className="upload-text-asset-body">
				<ProgressIndicator isLoading={isUploading} description="Uploading Text" />
				{$errorDisplay}
				{$content}
			</div>
		);
	};

	const renderFooter = () => {
		const disableCancel = isUploading;
		const disableUpload = !formData || isUploading;

		return (
			<>
				<Button theme="grey" onClick={onClose} disabled={disableCancel} >Cancel</Button>
				<Button theme="green" onClick={handleUpload} disabled={disableUpload} >Upload</Button>
			</>
		);
	};

	const bladeProps = {
		verticallyCenter: true,
	};

	const modalProps = {
		type: "modal",
		title: "Upload Text",
		className: "upload-text-asset-modal",
		allowFullScreen: false,
		setAppTitle: false,
		xClose: true,
		bladeProps,
		onClose,
		renderContent,
		renderFooter,
	};

	return (
		<Blade {...modalProps} />
	);
}

UploadTextAssetModal.displayName = "UploadTextAssetModal";
